'use client'

import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import Image from 'next/image'
import { Fragment, useMemo, useState } from 'react'

import { useHasMounted } from '@src/hooks/useHasMounted'
import { useLocale } from '@src/i18n/LocaleProvider'
import { getLocales } from '@src/i18n/locale.util'
import { cn } from '@src/utils/tailwind'

import { LocalizedLink } from '../LocalizedLink/LocalizedLink'

const locales = getLocales()

export const LocaleSwitcher = () => {
  const { locale } = useLocale()
  const [localeSelected, setLocaleSelected] = useState(locale)
  const isMounted = useHasMounted()

  const localesSorted = useMemo(() => {
    return [...locales.filter((l) => l.code !== localeSelected.code)].filter(Boolean)
  }, [localeSelected.code])

  const renderOptions = () => {
    if (!isMounted) return null

    return localesSorted.map((l) => {
      const alternateTagSelector = `[rel="alternate"][hreflang="${l.lang}-${l.countryCodeLowercase}"]`
      const alternateTag = globalThis.document?.head.querySelector(alternateTagSelector)
      const homepageUrl = `${location.origin}/${l.countryCodeLowercase}/${l.lang}`

      const attributeHref = alternateTag?.getAttribute('href')
      const url = new URL(attributeHref ?? homepageUrl, window.location.origin)
      url.search = attributeHref ? window.location.search : ''
      const href = url.toString()

      return (
        <MenuItem key={l.switcherLabel}>
          <LocalizedLink
            useNextLink={false}
            href={href}
            targetCountry={l.countryCodeLowercase}
            targetLanguage={l.lang}
            onClick={() => setLocaleSelected(l)}
            className="flex w-full cursor-pointer appearance-none items-center gap-3 rounded-md bg-bodi-white px-3 py-2 text-body-sm hover:bg-bodi-green-bright-500"
          >
            <Image priority src={l.flag} width={20} height={20} alt="flag" className="shrink-0" />
            <p className="min-w-fit">{l.switcherLabel}</p>
          </LocalizedLink>
        </MenuItem>
      )
    })
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        className={cn(
          'group z-20 inline-flex w-full items-center rounded-md px-4 py-2 text-body-sm transition-all',
          'data-open:rounded-t-none data-open:bg-bodi-white data-open:font-semibold'
        )}
      >
        <Image
          priority
          src={localeSelected.flag}
          width={20}
          height={20}
          alt="flag"
          className="shrink-0"
        />
        <p className="mr-1 ml-3">{localeSelected.switcherLabel}</p>
        <ChevronDownIcon className="w-6 shrink-0 transition-all group-data-open:rotate-180" />
      </MenuButton>

      <Transition as={Fragment}>
        <MenuItems
          className={cn(
            'absolute bottom-full left-0 z-10 w-full origin-bottom-right rounded-t-md bg-white transition-all duration-150 focus:outline-none',
            'translate-y-0 opacity-100',
            'data-closed:translate-y-4 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in'
          )}
        >
          <div className="p-1 pb-0">{renderOptions()}</div>
        </MenuItems>
      </Transition>
    </Menu>
  )
}
